import React, { useEffect, useState } from "react";
import AdmissionDocuments from "../updatedocuments/admissionDocuments";
import DuringTreatmentDocuments from "../updatedocuments/duringTreatmentDocuments";
import OnDischargeDocuments from "../updatedocuments/onDischargeDocuments";
import EnhancementDocuments from "../updatedocuments/EnhancementDocuments";
import ReviewDocumentModel from "../updatedocuments/reviewDocumentModal";
import { CaseServices } from "../../../services/CaseServices";
import { CircularProgress } from "@mui/material";
import { docUploadRefreshContext } from "../contexts/CaseContexts";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

import ICD10DiagnosisSearch from "../../../ICD/ICD10DiagnosisSearch";
import DownloadTemplateButton from "../updatedocuments/DownloadTemplateButton";
import FillTemplateModal from "../updatedocuments/FillTemplateModal";
// import UploadTemplateButton from "../updatedocuments/UploadTemplateButton";
// import UploadedTemplateCard from "../updatedocuments/UploadedTemplateCard";
import AddIcon from "@mui/icons-material/Add";
import DrawIcon from "@mui/icons-material/Draw";
import { toast } from "react-toastify";
import "./AddDocuments.scss";

const isPreauthTemplateDoc = (doc) => {
  if (!doc) return false;
  const name = String(doc.documentName || "")
    .trim()
    .toLowerCase();
  // Support new name and older "Template" mappings already on cases
  return name === "preauth template" || name === "template";
};

const isPreauthTemplateApproved = (doc) => {
  if (!isPreauthTemplateDoc(doc)) return false;
  return String(doc.status || "").toUpperCase() === "APPROVED";
};

const AddDocuments = ({
  selectedProcedureId,
  hsaStage,
  caseId,
  functionalityType,
  payer,
  refreshPage,
  onOpenSecondStageApproval,
  isTpa = false,
}) => {
  const isEditFlow = functionalityType === "EDIT";
  const showTpaActions = isEditFlow && isTpa;
  const [openDiagnosisSearch, setOpenDiagnosisSearch] = useState(false);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState([]);
  const [savingDiagnoses, setSavingDiagnoses] = useState(false);
  const [procdureDocuments, setProcedureDocuments] = useState({
    ADMISSIONDOCUMENT: [],
    DURINGTREATMENTDOCUMENT: [],
    ONDISCHARGEDOCUMENT: [],
  });
  const [enhancementDocuments, setEnhancementDocuments] = useState(null);
  const [isLoading, setIsloading] = useState(true);
  const [latestStage, setLatestStage] = useState(null);
  const [latestEnhancement, setLatestEnhancement] = useState(null);
  const [tpaCaseCredentials, setTpaCaseCredentials] = useState(null);
  const [showCredentials, setShowCredentials] = useState(false);
  // Multi-document review session: { ids: [...docIds], currentId }
  const [reviewSession, setReviewSession] = useState(null);
  const [fillTemplateOpen, setFillTemplateOpen] = useState(false);

  // `silent: true` refreshes data without unmounting the page behind the loader,
  // so an open review modal keeps its place.
  async function getMappedDocuments(options) {
    const silent = options?.silent === true;
    let freshMappedDocuments = null;
    try {
      if (!silent) setIsloading(true);
      const documentType = hsaStage?.toLowerCase()?.includes("admission")
        ? "ADMISSIONDOCUMENT"
        : hsaStage?.toLowerCase()?.includes("enhancement")
          ? "ENHANCEMENT"
          : "DISCHARGEDOCUMENT";
      let documents = await CaseServices.getMappedDocuments(
        caseId,
        documentType,
      );
      if (documents?.data?.mappedDocuments) {
        freshMappedDocuments = documents?.data?.mappedDocuments;
        setProcedureDocuments(documents?.data?.mappedDocuments);
        //Below code is for getting all the enhancements done
        const enhancementDocumentsData = [];
        for (let i in documents?.data?.mappedDocuments) {
          if (i.toLowerCase().includes("enhancement")) {
            enhancementDocumentsData.push({
              key: i,
              value: documents?.data?.mappedDocuments[i],
            });
          }
        }
        //sort the enhancement detail info based on enhancement number in descending order
        enhancementDocumentsData?.sort((a, b) => {
          const numA = parseInt(a.key.replace(/[^\d]/g, ""));
          const numB = parseInt(b.key.replace(/[^\d]/g, ""));

          return numB - numA;
        });
        setEnhancementDocuments(enhancementDocumentsData);
      }
      if (!silent) setIsloading(false);
    } catch (error) {
      handleApiErrorResponses(error);
      if (!silent) setIsloading(false);
    }
    return freshMappedDocuments;
  }

  // Start a review session from a document card. `siblings` is the group the
  // document belongs to (admission list, claim list or one enhancement's list).
  const openReview = (doc, siblings = []) => {
    if (!doc?.id) return;
    const ids = (siblings || [])
      .filter((d) => d?.status === "INREVIEW")
      .map((d) => d.id);
    if (!ids.includes(doc.id)) ids.push(doc.id);
    setReviewSession({ ids, currentId: doc.id });
  };

  const goToNextReviewDocument = () => {
    setReviewSession((prev) => {
      if (!prev) return prev;
      const idx = prev.ids.indexOf(prev.currentId);
      if (idx < 0 || idx >= prev.ids.length - 1) return prev;
      return { ...prev, currentId: prev.ids[idx + 1] };
    });
  };

  const goToPrevReviewDocument = () => {
    setReviewSession((prev) => {
      if (!prev) return prev;
      const idx = prev.ids.indexOf(prev.currentId);
      if (idx <= 0) return prev;
      return { ...prev, currentId: prev.ids[idx - 1] };
    });
  };

  const closeReviewSession = () => setReviewSession(null);

  // After a whole-document approve/reject: refresh data in the background and
  // stay on the same document (user presses Next to continue).
  // Returns true when no document of the session is left in review, so the
  // modal can close itself automatically.
  const handleReviewActionComplete = async () => {
    const fresh = await getMappedDocuments({ silent: true });
    if (!fresh || !reviewSession) return false;
    const statusById = new Map();
    Object.values(fresh).forEach((group) => {
      if (Array.isArray(group)) {
        group.forEach((d) => d?.id && statusById.set(d.id, d.status));
      }
    });
    return !reviewSession.ids.some((id) => statusById.get(id) === "INREVIEW");
  };

  const getLatestCaseStage = async () => {
    try {
      const response = await CaseServices.getLatestCaseStage(caseId);
      if (response?.data?.currnetSubMasterStage[0]) {
        setLatestStage(response?.data?.currnetSubMasterStage[0]?.hsaStage);

        setLatestEnhancement(response?.data?.latestEnhancement);
      }
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };
  const getTpaCaseCredentials = async () => {
    try {
      const response = await CaseServices.getTpaCaseCredentials(caseId);
      setTpaCaseCredentials(response.data);
    } catch (error) {
      handleApiErrorResponses(error);
      setTpaCaseCredentials(null);
    }
  };
  // Older cases store a single diagnosis object, newer ones an array
  const toDiagnosisList = (primaryDiagnosisDetails) => {
    if (!primaryDiagnosisDetails) return [];
    const list = Array.isArray(primaryDiagnosisDetails)
      ? primaryDiagnosisDetails
      : [primaryDiagnosisDetails];
    return list
      .filter((item) => item?.code)
      .map((item) => ({
        code: item.code,
        title: item.title || item.value || item.selectedText || "",
      }));
  };

  const getSavedDiagnoses = async () => {
    try {
      const response = await CaseServices.getCaseDetails(caseId);
      setSelectedDiagnoses(
        toDiagnosisList(response?.data?.caseDetails?.primaryDiagnosisDetails),
      );
    } catch (error) {
      handleApiErrorResponses(error);
    }
  };

  const handleSaveDiagnoses = async (diagnoses) => {
    if (savingDiagnoses) return;
    if (!diagnoses?.length) return;

    setSavingDiagnoses(true);
    try {
      // TPA: save all selected diagnoses as an array (chips show each one)
      const primaryDiagnosisDetails = diagnoses.map((selected, index) => ({
        iNo: index + 1,
        code: selected.code,
        title: selected.title,
        value: selected.title,
        selectedText: selected.title,
        bestMatchText: selected.title,
      }));
      await CaseServices.updateCase({ primaryDiagnosisDetails }, caseId);
      setSelectedDiagnoses(diagnoses);
      setOpenDiagnosisSearch(false);
      toast.success(
        diagnoses.length > 1
          ? "Diagnoses updated successfully"
          : "Diagnosis updated successfully",
      );
      if (refreshPage) {
        await refreshPage();
      }
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSavingDiagnoses(false);
    }
  };

  useEffect(() => {
    getMappedDocuments();
    getLatestCaseStage();
    if (showTpaActions) {
      getSavedDiagnoses();
      getTpaCaseCredentials();
    }
  }, []);

  // Preauth Template comes from HSA as PENDING; CC uploads & approves via document card.
  // Enable Submit only when Preauth Template is APPROVED AND diagnosis is added.
  const allMappedDocs = [
    ...(procdureDocuments?.ADMISSIONDOCUMENT || []),
    ...(procdureDocuments?.DURINGTREATMENTDOCUMENT || []),
    ...(procdureDocuments?.ONDISCHARGEDOCUMENT || []),
    ...(enhancementDocuments || []).flatMap((item) => item?.value || []),
  ];
  const reviewDoc = reviewSession
    ? allMappedDocs.find((d) => d?.id === reviewSession.currentId)
    : null;
  const reviewIndex = reviewSession
    ? reviewSession.ids.indexOf(reviewSession.currentId)
    : -1;

  // Close the session if the current document disappeared (e.g. deleted)
  useEffect(() => {
    if (reviewSession && !isLoading && !reviewDoc) {
      setReviewSession(null);
    }
  }, [reviewSession, reviewDoc, isLoading]);

  const isTemplateApproved = allMappedDocs.some(isPreauthTemplateApproved);
  const preauthTemplateDoc = allMappedDocs.find(isPreauthTemplateDoc);
  const isDiagnosisAdded = selectedDiagnoses.length > 0;
  // Template/diagnosis checks apply only to Admission stages.
  // Claim and Enhancement stages can always submit.
  const currentStage = (hsaStage || latestStage || "").toLowerCase();
  const isNonAdmissionStage =
    currentStage.includes("claim") ||
    currentStage.includes("enhancement") ||
    currentStage.includes("discharge");

  // Documents of the current stage that must all be APPROVED before submitting
  const currentStageDocs = currentStage.includes("admission")
    ? procdureDocuments?.ADMISSIONDOCUMENT || []
    : currentStage.includes("enhancement")
      ? enhancementDocuments?.[0]?.value || [] // latest enhancement (sorted desc)
      : [
          ...(procdureDocuments?.DURINGTREATMENTDOCUMENT || []),
          ...(procdureDocuments?.ONDISCHARGEDOCUMENT || []),
        ];
  const unapprovedDocs = currentStageDocs.filter(
    (d) => String(d?.status || "").toUpperCase() !== "APPROVED",
  );
  const areDocumentsApproved = unapprovedDocs.length === 0;

  const canSubmit =
    areDocumentsApproved &&
    (isNonAdmissionStage ? true : isTemplateApproved && isDiagnosisAdded);

  const isEnhancementStage =
    enhancementDocuments &&
    enhancementDocuments.length > 0 &&
    latestStage &&
    latestStage?.toLowerCase().includes("enhancement");

  return (
    <div className="add-document-box">
      {!isLoading ? (
        <docUploadRefreshContext.Provider
          value={{ getMappedDocuments, openReview }}
        >
          {showTpaActions && (
            <div className="flex justify-between items-center gap-3 mb-4 flex-wrap">
              {payer ? (
                <div
                  style={{ border: "1px solid #017C57" }}
                  className="text-sm font-semibold text-[#017C57] px-4 py-2 rounded-full bg-white max-w-full truncate"
                  title={payer}
                >
                  Payer: {payer}
                </div>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-3">
                {showCredentials && (
                  <div className="flex flex-col gap-2 border border-solid border-gray-300 rounded-md p-4">
                    <div className="flex items-center gap-2">
                      <span>Username:</span>
                      <span>
                        {tpaCaseCredentials?.hospitalTpaCredential?.userName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>Password:</span>
                      <span>
                        {tpaCaseCredentials?.hospitalTpaCredential?.password}
                      </span>
                    </div>
                    <div
                      style={{
                        color: "#017C57",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        const getExternalUrl = (rawUrl) => {
                          const url = rawUrl.trim();
                          if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url))
                            return url; // already has protocol
                          if (url.startsWith("//")) return `https:${url}`;
                          return `https://${url}`;
                        };
                        const url = getExternalUrl(
                          tpaCaseCredentials?.tpaMaster?.urlOrReceiverMail,
                        );
                        if (url) {
                          window.open(url, "_blank", "noopener,noreferrer");
                        }
                      }}
                    >
                      Click Here to Login to TPA Portal and upload documents
                    </div>
                  </div>
                )}
                <DownloadTemplateButton payer={payer} />
                <div
                  className="self-center flex items-center gap-2 text-sm text-white font-semibold bg-[#017C57] px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
                  onClick={() => setFillTemplateOpen(true)}
                >
                  <DrawIcon sx={{ fontSize: 18 }} />
                  Fill Template
                </div>
                {/* Upload Template — disabled; uploaded via mapped PENDING Preauth Template card */}
                {/* <UploadTemplateButton onUploaded={setUploadedTemplate} /> */}
              </div>
            </div>
          )}
          {isEnhancementStage ? (
            <EnhancementDocuments
              ENHANCEMENTDOCUMENTS={enhancementDocuments}
              latestEnhancement={latestEnhancement}
            />
          ) : (
            <>
              {latestStage &&
                latestStage?.toLowerCase().includes("admission") && (
                  <AdmissionDocuments
                    ADMISSIONDOCUMENT={procdureDocuments?.ADMISSIONDOCUMENT}
                  />
                )}

              {/* <DuringTreatmentDocuments
                DURINGTREATMENTDOCUMENT={
                  procdureDocuments?.DURINGTREATMENTDOCUMENT
                }
              /> */}
              {((latestStage && latestStage?.toLowerCase().includes("claim")) ||
                latestStage?.toLowerCase().includes("discharge")) && (
                <OnDischargeDocuments
                  ONDISCHARGEDOCUMENT={procdureDocuments?.ONDISCHARGEDOCUMENT}
                />
              )}
            </>
          )}
          {showTpaActions && (
            <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
              <div className="flex flex-wrap items-center gap-3">
                <div
                  className="flex items-center gap-2 text-sm text-white font-semibold bg-[#017C57] px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
                  onClick={() => setOpenDiagnosisSearch(true)}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                  Add Diagnosis
                </div>
                {selectedDiagnoses.map((item) => (
                  <div
                    key={item.code}
                    title={item.title}
                    style={{ border: "1px solid #017C57" }}
                    className="flex items-center gap-2 max-w-[280px] px-3 py-1.5 rounded-full bg-white text-sm"
                  >
                    <span className="font-semibold text-[#017C57]">
                      {item.code}
                    </span>
                    <span className="truncate">{item.title}</span>
                  </div>
                ))}
              </div>
              <div
                className={`text-sm text-white font-semibold bg-[#017C57] px-6 py-2 rounded-full whitespace-nowrap ${
                  canSubmit ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
                }`}
                onClick={() => {
                  if (!canSubmit) return;
                  // toast.success(
                  //   "Patient related email will be sent to TPA",
                  // );
                  onOpenSecondStageApproval?.();
                  if (!tpaCaseCredentials?.tpaMaster?.isRpa)
                    setShowCredentials(true);
                }}
                title={
                  canSubmit
                    ? undefined
                    : !areDocumentsApproved
                      ? "All documents must be approved before submitting"
                      : !isTemplateApproved && !isDiagnosisAdded
                        ? "Approve Preauth Template and add Diagnosis before submitting"
                        : !isTemplateApproved
                          ? "Preauth Template must be Approved before submitting"
                          : "Add Diagnosis before submitting"
                }
              >
                Submit
              </div>
            </div>
          )}
        </docUploadRefreshContext.Provider>
      ) : (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginTop: "100px",
          }}
        >
          <CircularProgress />
        </div>
      )}
      {/* Review modal lives outside the loading gate so background refreshes
          never unmount it while the user moves between documents. */}
      {reviewSession && reviewDoc && (
        <docUploadRefreshContext.Provider
          value={{
            getMappedDocuments: () => getMappedDocuments({ silent: true }),
          }}
        >
          <ReviewDocumentModel
            // Remount per document (and when its images change) so all
            // per-document state resets and fresh images are shown.
            key={`${reviewDoc.id}:${(reviewDoc.documenturlmasters || [])
              .map((img) => img?.id)
              .join(",")}`}
            documentDetails={reviewDoc}
            type="review"
            showTrigger={false}
            initialOpen
            hasNext={reviewIndex < reviewSession.ids.length - 1}
            onNext={goToNextReviewDocument}
            hasPrev={reviewIndex > 0}
            onPrev={goToPrevReviewDocument}
            reviewIndex={reviewIndex}
            reviewTotal={reviewSession.ids.length}
            onActionComplete={handleReviewActionComplete}
            onClose={closeReviewSession}
          />
        </docUploadRefreshContext.Provider>
      )}
      {openDiagnosisSearch && (
        <ICD10DiagnosisSearch
          initialSelected={selectedDiagnoses}
          onClose={() => setOpenDiagnosisSearch(false)}
          onProceed={handleSaveDiagnoses}
        />
      )}
      {fillTemplateOpen && (
        <FillTemplateModal
          open={fillTemplateOpen}
          onClose={() => setFillTemplateOpen(false)}
          payer={payer}
          preauthDocumentId={preauthTemplateDoc?.id}
          preauthDocument={preauthTemplateDoc}
          caseDocuments={allMappedDocs}
          onUploaded={() => getMappedDocuments({ silent: true })}
        />
      )}
    </div>
  );
};

export default AddDocuments;
